import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, Navigate, useLocation } from 'react-router-dom'
import {
  BookOpen,
  Briefcase,
  Building2,
  ExternalLink,
  FileText,
  HelpCircle,
  Home,
  Inbox,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Users,
  X,
} from 'lucide-react'
import { useAdminAuth } from './AuthContext'

const STORAGE_KEY = 'digisutra-cms-sidebar-collapsed'

const PAGE_TITLES: Array<{ match: RegExp; title: string; subtitle?: string }> = [
  { match: /^\/admin\/?$/, title: 'Home', subtitle: 'Website CMS overview' },
  { match: /^\/admin\/insights\/new\/?$/, title: 'New insight', subtitle: 'Write & SEO' },
  { match: /^\/admin\/insights\/[^/]+\/edit\/?$/, title: 'Edit insight', subtitle: 'Write & SEO' },
  { match: /^\/admin\/insights\/[^/]+\/?$/, title: 'View insight', subtitle: 'Preview' },
  { match: /^\/admin\/insights/, title: 'Insights', subtitle: 'Blog posts & articles' },
  { match: /^\/admin\/case-studies\/new\/?$/, title: 'New case study', subtitle: 'Write & SEO' },
  { match: /^\/admin\/case-studies\/[^/]+\/edit\/?$/, title: 'Edit case study', subtitle: 'Write & SEO' },
  { match: /^\/admin\/case-studies\/[^/]+\/?$/, title: 'View case study', subtitle: 'Preview' },
  { match: /^\/admin\/case-studies/, title: 'Case studies', subtitle: 'Client success stories' },
  { match: /^\/admin\/team\/new\/?$/, title: 'New team member', subtitle: 'Photo & profile' },
  { match: /^\/admin\/team\/[^/]+\/edit\/?$/, title: 'Edit team member', subtitle: 'Photo & profile' },
  { match: /^\/admin\/team/, title: 'Team', subtitle: 'Team members' },
  { match: /^\/admin\/clients\/new\/?$/, title: 'New client', subtitle: 'Logo & details' },
  { match: /^\/admin\/clients\/[^/]+\/edit\/?$/, title: 'Edit client', subtitle: 'Logo & details' },
  { match: /^\/admin\/clients/, title: 'Clients', subtitle: 'Logo grid' },
  { match: /^\/admin\/faqs/, title: 'FAQs', subtitle: 'Manage FAQs by page / city location' },
  { match: /^\/admin\/policies/, title: 'Policies', subtitle: 'Edit Privacy, Terms & Refund pages' },
  { match: /^\/admin\/settings/, title: 'Settings', subtitle: 'Brand, contact, address, map & social' },
  { match: /^\/admin\/submissions/, title: 'Submissions', subtitle: 'Contact form inbox & lead status' },
]

function titleForPath(pathname: string) {
  const hit = PAGE_TITLES.find((p) => p.match.test(pathname))
  return hit || { title: 'Website CMS', subtitle: 'Manage digisutra.in' }
}

const links = [
  { to: '/admin', label: 'Home', end: true, icon: Home },
  { to: '/admin/insights', label: 'Insights', icon: BookOpen },
  { to: '/admin/case-studies', label: 'Case studies', icon: Briefcase },
  { to: '/admin/team', label: 'Team', icon: Users },
  { to: '/admin/clients', label: 'Clients', icon: Building2 },
  { to: '/admin/faqs', label: 'FAQs', icon: HelpCircle },
  { to: '/admin/policies', label: 'Policies', icon: FileText },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
  { to: '/admin/submissions', label: 'Submissions', icon: Inbox },
]

function useCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  return { collapsed, toggle }
}

export function AdminShell() {
  const { user, loading, logout } = useAdminAuth()
  const { collapsed, toggle } = useCollapsed()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { pathname } = useLocation()
  const pageMeta = useMemo(() => titleForPath(pathname), [pathname])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  if (loading) {
    return (
      <div className="min-h-screen bg-[linear-gradient(165deg,#fafbff_0%,#eef2ff_35%,#ffffff_70%,#f8fafc_100%)] p-6 sm:p-10">
        <div className="mx-auto max-w-5xl space-y-6">
          <div className="flex gap-3 items-center">
            <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200" />
            <div className="h-5 w-40 animate-pulse rounded-lg bg-slate-200" />
            <div className="ml-auto h-9 w-24 animate-pulse rounded-xl bg-slate-200" />
          </div>
          <div className="h-36 w-full animate-pulse rounded-3xl bg-slate-200/80" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-200/70" />
            ))}
          </div>
        </div>
      </div>
    )
  }
  if (!user) return <Navigate to="/admin/login" replace />

  return (
    <div className="min-h-screen flex bg-[linear-gradient(165deg,#fafbff_0%,#eef2ff_28%,#ffffff_65%,#f8fafc_100%)] text-slate-900">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex sticky top-0 h-screen shrink-0 flex-col overflow-hidden bg-navy-900 text-slate-100 shadow-[8px_0_32px_-12px_rgba(15,23,42,0.35)] transition-[width] duration-200 ease-out ${
          collapsed ? 'w-[68px]' : 'w-60'
        }`}
      >
        <SidebarChrome
          collapsed={collapsed}
          userName={user.name}
          userEmail={user.email}
          onLogout={() => logout()}
          onToggle={toggle}
          onNavigate={() => undefined}
        />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm"
            aria-label="Close menu overlay"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col overflow-hidden bg-navy-900 text-slate-100 shadow-2xl">
            <button
              type="button"
              className="absolute right-3 top-4 z-10 rounded-xl p-1.5 text-slate-300 hover:bg-white/10"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarChrome
              collapsed={false}
              userName={user.name}
              userEmail={user.email}
              onLogout={() => logout()}
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col min-h-[100dvh]">
        <header
          className="sticky top-0 z-30 flex items-center gap-3 border-b border-primary/10 bg-white/85 px-4 py-3 backdrop-blur-xl md:px-6"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/15 bg-white text-primary md:hidden active:scale-95 transition"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={toggle}
            className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-xl border border-primary/15 bg-white text-primary transition hover:bg-primary/[0.06]"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>

          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-base sm:text-lg font-bold tracking-tight text-navy-900">
              {pageMeta.title}
            </h1>
            {pageMeta.subtitle && (
              <p className="hidden sm:block truncate text-xs text-slate-500">{pageMeta.subtitle}</p>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 text-sm text-slate-500">
            <span className="hidden xl:inline truncate max-w-[200px]">{user.email}</span>
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-primary/15 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/[0.06] active:scale-95"
            >
              <span className="hidden sm:inline">Website</span>
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8 pb-[calc(5.25rem+env(safe-area-inset-bottom))] md:pb-8">
          <div className="mx-auto w-full max-w-none">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom app tab bar */}
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-primary/10 bg-white/95 backdrop-blur-xl md:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Primary"
        >
          <div className="grid grid-cols-5 gap-0.5 px-1 pt-1.5 pb-1.5">
            {mobileTabs.map((t) => {
              const Icon = t.icon
              if (t.more) {
                return (
                  <button
                    key="more"
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 text-[0.65rem] font-medium text-slate-500 active:bg-primary/[0.06]"
                  >
                    <Menu className="h-5 w-5" aria-hidden />
                    More
                  </button>
                )
              }
              return (
                <NavLink
                  key={t.to}
                  to={t.to!}
                  end={t.end}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 text-[0.65rem] font-medium transition active:scale-95 ${
                      isActive ? 'text-primary' : 'text-slate-500'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          isActive ? 'bg-accent text-navy-900 shadow-sm' : ''
                        }`}
                      >
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      <span>{t.label}</span>
                    </>
                  )}
                </NavLink>
              )
            })}
          </div>
        </nav>
      </div>
    </div>
  )
}

const mobileTabs: Array<{
  to?: string
  label: string
  end?: boolean
  icon: typeof Home
  more?: boolean
}> = [
  { to: '/admin', label: 'Home', end: true, icon: Home },
  { to: '/admin/insights', label: 'Insights', icon: BookOpen },
  { to: '/admin/case-studies', label: 'Cases', icon: Briefcase },
  { to: '/admin/submissions', label: 'Leads', icon: Inbox },
  { label: 'More', icon: Menu, more: true },
]

function SidebarChrome({
  collapsed,
  userName,
  userEmail,
  onLogout,
  onToggle,
  onNavigate,
}: {
  collapsed: boolean
  userName: string
  userEmail: string
  onLogout: () => void
  onToggle?: () => void
  onNavigate: () => void
}) {
  const initials =
    userName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'

  return (
    <>
      {/* Brand */}
      <div
        className={`flex shrink-0 items-center border-b border-white/10 ${
          collapsed ? 'h-14 justify-center px-2' : 'h-16 justify-between gap-2 px-4'
        }`}
      >
        <Link
          to="/admin"
          onClick={onNavigate}
          className={`flex items-center overflow-hidden ${collapsed ? 'justify-center' : 'min-w-0 flex-1'}`}
          title="DigiSutra CMS"
        >
          {collapsed ? (
            <img src="/favicon.png" alt="DigiSutra" className="h-8 w-8 rounded-lg object-contain" />
          ) : (
            <img src="/logo-dark.png" alt="DigiSutra" className="h-8 w-auto max-w-[148px] object-contain object-left" />
          )}
        </Link>
        {!collapsed && onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 rounded-xl p-1.5 text-slate-400 transition hover:bg-white/10 hover:text-white"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav
        className={`flex-1 overflow-y-auto overflow-x-hidden ${
          collapsed ? 'px-2 py-3 space-y-1' : 'px-2.5 py-3 space-y-0.5'
        }`}
      >
        {!collapsed && (
          <p className="px-3 pb-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-slate-500">
            Content
          </p>
        )}
        {links.map((l) => {
          const Icon = l.icon
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              title={collapsed ? l.label : undefined}
              className={({ isActive }) =>
                [
                  'group relative flex items-center rounded-xl text-sm font-medium transition',
                  collapsed ? 'h-10 w-10 mx-auto justify-center' : 'gap-2.5 px-3 py-2.5',
                  isActive
                    ? 'bg-accent text-navy-900 shadow-[0_4px_14px_-2px_rgba(255,215,0,0.35)]'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white',
                ].join(' ')
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{l.label}</span>}
              {collapsed && (
                <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 z-[60] -translate-y-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-navy-800 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100">
                  {l.label}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Footer */}
      <div
        className={`shrink-0 border-t border-white/10 ${
          collapsed ? 'flex flex-col items-center gap-1.5 px-2 py-3' : 'space-y-2 p-3'
        }`}
      >
        {collapsed ? (
          <>
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-xs font-bold text-white"
              title={`${userName} · ${userEmail}`}
            >
              {initials}
            </div>
            <IconBtn title="Log out" onClick={onLogout} label="Log out">
              <LogOut className="h-4 w-4" />
            </IconBtn>
            {onToggle && (
              <IconBtn title="Expand sidebar" onClick={onToggle} label="Expand sidebar">
                <PanelLeftOpen className="h-4 w-4" />
              </IconBtn>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2.5 px-1">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-xs font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-white">{userName}</div>
                <div className="truncate text-xs text-slate-400">{userEmail}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 transition hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Log out
            </button>
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              onClick={onNavigate}
              className="flex items-center gap-1.5 px-1 py-1 text-xs text-slate-400 transition hover:text-accent"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              View website
            </Link>
          </>
        )}
      </div>
    </>
  )
}

function IconBtn({
  children,
  title,
  label,
  onClick,
}: {
  children: ReactNode
  title: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10 hover:text-white"
    >
      {children}
    </button>
  )
}
